import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// ─── Fiscal period helpers ────────────────────────────────

export type FiscalPeriodType = "MONTHLY" | "QUARTERLY" | "SEMI_ANNUAL" | "ANNUAL";

/** Returns the current period string based on the tenant's fiscal period type. */
export function getCurrentPeriod(
  type: FiscalPeriodType = "MONTHLY",
  date: Date = new Date(),
): string {
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  switch (type) {
    case "QUARTERLY":
      return `${year}-Q${Math.ceil(month / 3)}`;
    case "SEMI_ANNUAL":
      return `${year}-H${month <= 6 ? 1 : 2}`;
    case "ANNUAL":
      return `${year}`;
    case "MONTHLY":
    default:
      return `${year}-${String(month).padStart(2, "0")}`;
  }
}

/** Returns a list of the last 12 period options for selects, matching the fiscal type. */
export function getPeriodOptions(
  type: FiscalPeriodType = "MONTHLY",
): { value: string; label: string }[] {
  const options: { value: string; label: string }[] = [];
  const now = new Date();
  const seen = new Set<string>();

  // Walk back ~18 months to ensure we get 12 unique period values for any type
  for (let i = 0; i < 18 && options.length < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = getCurrentPeriod(type, d);
    if (seen.has(value)) continue;
    seen.add(value);

    let label: string;
    switch (type) {
      case "QUARTERLY": {
        const q = Math.ceil((d.getMonth() + 1) / 3);
        label = `Q${q} ${d.getFullYear()}`;
        break;
      }
      case "SEMI_ANNUAL": {
        const h = d.getMonth() < 6 ? 1 : 2;
        label = `H${h} ${d.getFullYear()}`;
        break;
      }
      case "ANNUAL":
        label = `${d.getFullYear()}`;
        break;
      case "MONTHLY":
      default:
        label = d.toLocaleDateString("en-US", { year: "numeric", month: "long" });
        break;
    }
    options.push({ value, label });
  }

  return options;
}

/**
 * Expands any period format into a sorted list of YYYY-MM month strings.
 * Used by the recalculate orchestrator to chunk multi-period selections into
 * individual monthly API requests.
 *
 * "2026-04"  → ["2026-04"]
 * "2026-Q1"  → ["2026-01", "2026-02", "2026-03"]
 * "2026-H2"  → ["2026-07", "2026-08", "2026-09", "2026-10", "2026-11", "2026-12"]
 * "2026"     → ["2026-01", ..., "2026-12"]
 */
export function expandToMonths(period: string): string[] {
  // Already a YYYY-MM month string
  if (/^\d{4}-\d{2}$/.test(period)) return [period];

  const year = parseInt(period.slice(0, 4), 10);

  const quarterMatch = /^(\d{4})-Q([1-4])$/.exec(period);
  if (quarterMatch) {
    const q = parseInt(quarterMatch[2]!, 10);
    const startMonth = (q - 1) * 3 + 1;
    return Array.from(
      { length: 3 },
      (_, i) => `${year}-${String(startMonth + i).padStart(2, "0")}`,
    );
  }

  const halfMatch = /^(\d{4})-H([12])$/.exec(period);
  if (halfMatch) {
    const h = parseInt(halfMatch[2]!, 10);
    const startMonth = (h - 1) * 6 + 1;
    return Array.from(
      { length: 6 },
      (_, i) => `${year}-${String(startMonth + i).padStart(2, "0")}`,
    );
  }

  // Annual "2026"
  if (/^\d{4}$/.test(period)) {
    return Array.from({ length: 12 }, (_, i) => `${year}-${String(i + 1).padStart(2, "0")}`);
  }

  return [period];
}

/** Returns the last N months as YYYY-MM strings, newest first. */
export function getRecentMonths(count = 18): { value: string; label: string }[] {
  const now = new Date();
  return Array.from({ length: count }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const label = d.toLocaleDateString("en-US", { year: "numeric", month: "long" });
    return { value, label };
  });
}
